import { useMemo, useState } from "react";
import { Plus, Search, Save, X, Pencil, Trash2 } from "lucide-react";
import { TEST_IDS } from "@/testIds";
import { formatMoney, formatPct, monthIsoToInput, monthInputToIso, monthLabel } from "@/lib/format";
import { useIsMobile } from "@/lib/useMediaQuery";

/**
 * Reusable CRUD data table with mobile-friendly stacked card view.
 *
 * columns = [{ key, label, type, options?, width?, align?, format?, default? }]
 * `primaryLabel` — key used as the mobile card headline (defaults to first col)
 * `primaryMetric` — key used as the big right-aligned figure on mobile cards
 * onCreate/onUpdate/onDelete — async fns
 */
export default function DataTable({
  module,
  columns,
  rows,
  onCreate,
  onUpdate,
  onDelete,
  searchable = true,
  emptyMessage = "No rows yet — click Add to enter your first record.",
  primaryLabel,
  primaryMetric,
}) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      columns.some((c) => {
        const v = r[c.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [rows, columns, search]);

  const startCreate = () => {
    const blank = Object.fromEntries(
      columns.map((c) => [c.key, c.default ?? (c.type === "number" || c.type === "money" || c.type === "pct" ? 0 : "")])
    );
    setDraft(blank);
    setEditingId(null);
    setCreating(true);
  };

  const startEdit = (row) => {
    setDraft({ ...row });
    setEditingId(row.id || row._id);
    setCreating(false);
  };

  const cancel = () => {
    setDraft({});
    setEditingId(null);
    setCreating(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      const cleanDraft = Object.fromEntries(Object.entries(draft).filter(([k]) => !k.startsWith("_")));
      if (creating) await onCreate?.(cleanDraft);
      else await onUpdate?.(editingId, cleanDraft);
      cancel();
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this row? This cannot be undone.")) return;
    setBusy(true);
    try {
      await onDelete?.(id);
    } finally {
      setBusy(false);
    }
  };

  const renderCell = (col, row) => {
    const v = row[col.key];
    if (col.format) return col.format(v, row);
    switch (col.type) {
      case "money":
        return <span className="num text-ink">{formatMoney(v)}</span>;
      case "pct":
        return <span className="num text-ink">{formatPct(v)}</span>;
      case "month":
        return <span className="num text-muted-dark">{v ? monthLabel(v) : "–"}</span>;
      default:
        return <span>{v ?? "–"}</span>;
    }
  };

  const renderInput = (col) => {
    const v = draft[col.key];
    const setV = (nv) => setDraft((d) => ({ ...d, [col.key]: nv }));
    if (col.type === "select") {
      return (
        <select
          value={v ?? ""}
          onChange={(e) => setV(e.target.value)}
          className="cf-touch w-full px-2 py-2 bg-paper rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink/15"
        >
          <option value="">—</option>
          {(col.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    if (col.type === "month") {
      return (
        <input
          type="month"
          value={monthIsoToInput(v)}
          onChange={(e) => setV(monthInputToIso(e.target.value))}
          className="cf-touch w-full px-2 py-2 bg-paper rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink/15 num"
        />
      );
    }
    if (col.type === "number" || col.type === "money") {
      return (
        <input
          type="number"
          step="0.01"
          value={v ?? 0}
          onChange={(e) => setV(parseFloat(e.target.value) || 0)}
          className="cf-touch w-full px-2 py-2 bg-paper rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink/15 num text-right"
        />
      );
    }
    if (col.type === "pct") {
      return (
        <input
          type="number"
          step="0.001"
          value={v ?? 0}
          onChange={(e) => setV(parseFloat(e.target.value) || 0)}
          placeholder="e.g. 0.075"
          className="cf-touch w-full px-2 py-2 bg-paper rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink/15 num text-right"
        />
      );
    }
    return (
      <input
        type="text"
        value={v ?? ""}
        onChange={(e) => setV(e.target.value)}
        className="cf-touch w-full px-2 py-2 bg-paper rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink/15"
      />
    );
  };

  const pKey = primaryLabel || columns[0]?.key;
  const mKey = primaryMetric;

  return (
    <div className="cf-card animate-fade-up overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center gap-3 px-5 py-4">
        {searchable && (
          <div className="relative flex-1 md:max-w-sm">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-light" />
            <input
              data-testid={TEST_IDS.searchInput(module)}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="cf-touch w-full pl-8 pr-3 py-2 text-sm bg-paper rounded-lg focus:outline-none focus:ring-2 focus:ring-ink/15"
            />
          </div>
        )}
        <div className="flex items-center gap-3 md:ml-auto">
          <span className="text-xs text-muted-light num">{filtered.length} rows</span>
          <button
            data-testid={TEST_IDS.addRow(module)}
            onClick={startCreate}
            disabled={creating || editingId !== null}
            className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-full bg-ink text-paper hover:bg-ink/90 disabled:opacity-50 transition-colors"
            style={{ boxShadow: "0 4px 12px rgba(18,49,89,0.15)" }}
          >
            <Plus size={13} strokeWidth={2.5} /> Add row
          </button>
        </div>
      </div>

      {/* Desktop table */}
      {!isMobile && (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-light border-y border-hairline/60">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`font-semibold px-4 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}
                  style={c.width ? { width: c.width } : {}}
                >
                  {c.label}
                </th>
              ))}
              <th className="text-right font-semibold px-4 py-3 w-[110px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {creating && (
              <tr className="bg-paper/60 border-b border-hairline/60">
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2">{c.displayOnly ? <span className="text-muted-light text-xs px-2">auto</span> : renderInput(c)}</td>
                ))}
                <td className="px-3 py-2 text-right space-x-1">
                  <button data-testid={TEST_IDS.saveRow(module)} onClick={save} disabled={busy} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-md bg-ink text-paper hover:bg-ink/90 disabled:opacity-50">
                    <Save size={12} /> Save
                  </button>
                  <button data-testid={TEST_IDS.cancelRow(module)} onClick={cancel} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-md text-muted-dark hover:text-ink">
                    <X size={12} /> Cancel
                  </button>
                </td>
              </tr>
            )}
            {filtered.map((row, i) => {
              const id = row.id || row._id;
              const isEditing = editingId === id;
              return (
                <tr
                  key={id}
                  className="border-b border-hairline/40 last:border-0 hover:bg-surface-2/60 transition-colors cf-row-in"
                  style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 text-sm ${c.align === "right" ? "text-right" : "text-left"}`}>
                      {isEditing && !c.displayOnly ? renderInput(c) : renderCell(c, row)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    {isEditing ? (
                      <>
                        <button data-testid={TEST_IDS.saveRow(module)} onClick={save} disabled={busy} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-md bg-ink text-paper hover:bg-ink/90 disabled:opacity-50">
                          <Save size={12} /> Save
                        </button>
                        <button onClick={cancel} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-md text-muted-dark hover:text-ink">
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button data-testid={TEST_IDS.editRow(module, id)} onClick={() => startEdit(row)} disabled={creating || editingId !== null} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-md text-muted-dark hover:text-ink hover:bg-surface-2 disabled:opacity-40">
                          <Pencil size={12} />
                        </button>
                        <button data-testid={TEST_IDS.deleteRow(module, id)} onClick={() => del(id)} disabled={creating || editingId !== null} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-md text-muted-dark hover:text-red-600 hover:bg-red-50 disabled:opacity-40">
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !creating && (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-14 text-muted-light text-sm">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Mobile stacked card view */}
      {isMobile && (
      <div className="border-t border-hairline/40">
        {creating && (
          <div className="p-4 bg-paper/60 space-y-3 border-b border-hairline/40">
            {columns.map((c) => (
              <div key={c.key}>
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">
                  {c.label}
                </div>
                {renderInput(c)}
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <button data-testid={TEST_IDS.saveRow(module)} onClick={save} disabled={busy} className="cf-touch flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm rounded-lg bg-ink text-paper disabled:opacity-50">
                <Save size={13} /> Save
              </button>
              <button data-testid={TEST_IDS.cancelRow(module)} onClick={cancel} className="cf-touch px-4 py-2 text-sm rounded-lg text-muted-dark">
                Cancel
              </button>
            </div>
          </div>
        )}
        {filtered.map((row, i) => {
          const id = row.id || row._id;
          const isEditing = editingId === id;
          if (isEditing) {
            return (
              <div key={id} className="p-4 bg-paper/60 space-y-3 border-b border-hairline/40">
                {columns.map((c) => (
                  <div key={c.key}>
                    <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">
                      {c.label}
                    </div>
                    {renderInput(c)}
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <button onClick={save} disabled={busy} className="cf-touch flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm rounded-lg bg-ink text-paper disabled:opacity-50">
                    <Save size={13} /> Save
                  </button>
                  <button onClick={cancel} className="cf-touch px-4 py-2 text-sm rounded-lg text-muted-dark">Cancel</button>
                </div>
              </div>
            );
          }
          return (
            <div
              key={id}
              className="px-5 py-4 border-b border-hairline/40 last:border-0 cf-row-in"
              style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink text-[15px] truncate">
                    {row[pKey] ?? "—"}
                  </div>
                  <div className="mt-1.5 space-y-1">
                    {columns
                      .filter((c) => c.key !== pKey && c.key !== mKey)
                      .slice(0, 3)
                      .map((c) => (
                        <div key={c.key} className="flex items-center gap-2 text-xs">
                          <span className="text-muted-light uppercase tracking-widest text-[10px] font-semibold w-24 shrink-0">
                            {c.label}
                          </span>
                          <span className="text-muted-dark truncate">{renderCell(c, row)}</span>
                        </div>
                      ))}
                  </div>
                </div>
                {mKey && (
                  <div className="text-right shrink-0">
                    <div className="num text-lg text-ink font-semibold">
                      {renderCell(columns.find((c) => c.key === mKey) || {}, row)}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  data-testid={TEST_IDS.editRow(module, id)}
                  onClick={() => startEdit(row)}
                  className="cf-touch inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-paper text-muted-dark"
                >
                  <Pencil size={11} /> Edit
                </button>
                <button
                  data-testid={TEST_IDS.deleteRow(module, id)}
                  onClick={() => del(id)}
                  className="cf-touch inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full text-red-600 bg-red-50"
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && !creating && (
          <div className="px-5 py-14 text-center text-muted-light text-sm">
            {emptyMessage}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
